import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import { IconSize, IconSizeTable } from "PFComponents/icon";
import { Tooltip } from "PFComponents/tooltip";
import { CustomValue, Experience } from "PFTypes";

import { getRequirementViewBox } from "./constants";
import css from "./experience_level.module.scss";
import { ExperienceCommonProps, ParentChildCommonProps } from "./experience_level.types";
import { ExperienceLevelTooltipContent } from "./parts/experience_level_tooltip_content";
import { NoExperience } from "./parts/no_experience";
import { RatingDots, RatingDotsProps } from "./parts/rating_dots/rating_dots";

export type ExperienceLevelProps = Omit<RatingDotsProps, "color" | "size" | "shape"> & {
  isRequirement?: boolean;
  requiredExperience?: Experience | null;
  showTooltip?: boolean;
  customValue?: CustomValue;
  size?: IconSize;
} & ({ color?: never; theme?: "dark" | "light" } | { color?: RatingDotsProps["color"]; theme?: never }) &
  ExperienceCommonProps &
  ParentChildCommonProps;

export const ExperienceLevel = ({
  showTooltip = true,
  isRequirement,
  experience,
  requiredExperience,
  hasParentValues,
  hasChildValues,
  color,
  theme,
  size = "xxxs",
  customValue,
  experienceLevels,
  experienceLabelMap,
  ...ratingDotsProps
}: ExperienceLevelProps) => {
  const { themeVars } = useTheme();
  const THEMES = {
    light: {
      main: themeVars["paletteBlue0"],
      secondary: themeVars["paletteNeutral0"]
    },
    dark: {
      main: themeVars["paletteWhite0"],
      secondary: themeVars["paletteBlue2"]
    }
  };
  const { main, secondary } = theme ? THEMES[theme] : { ...THEMES.light, main: color };
  const showLevelWithRequirement = !isRequirement && !!requiredExperience;
  const showNoExperience = !isRequirement && !experience && requiredExperience;
  const height = IconSizeTable[size];
  const shape = isRequirement ? "rect" : "circle";
  const hasParentChildRelation = hasParentValues || hasChildValues;

  const content = (
    <>
      {showNoExperience ? (
        <NoExperience
          requiredExperience={requiredExperience}
          hasParentValues={hasParentValues}
          hasChildValues={hasChildValues}
          size={height}
          experienceLevels={experienceLevels}
        />
      ) : (
        <RatingDots
          shape={shape}
          experience={(isRequirement && requiredExperience) || experience}
          size={height}
          color={main}
          colorSecondary={secondary}
          experienceLevels={experienceLevels}
          {...ratingDotsProps}
        />
      )}
      {showLevelWithRequirement && (
        <RatingDots
          shape="rect"
          size={height / 2}
          viewBox={getRequirementViewBox(experienceLevels.length)}
          experience={requiredExperience}
          color={
            showNoExperience
              ? hasParentChildRelation
                ? themeVars["paletteOrange3"]
                : themeVars["paletteRed0"]
              : main
          }
          colorSecondary={secondary}
          experienceLevels={experienceLevels}
          {...ratingDotsProps}
        />
      )}
    </>
  );

  if (!showTooltip && !showLevelWithRequirement) {
    return content;
  }

  return (
    <Tooltip
      content={
        <ExperienceLevelTooltipContent
          shape={shape}
          experience={experience}
          hasParentValues={hasParentValues}
          hasChildValues={hasChildValues}
          requiredExperience={requiredExperience}
          customValue={customValue}
          experienceLevels={experienceLevels}
          experienceLabelMap={experienceLabelMap}
        />
      }
      disabled={!showTooltip}
      tooltipContainerProps={{ style: { maxWidth: 600 }, className: css.smallPadding }} // just larger than default value to not crop the content
      triggerContainerProps={{ className: css.experienceLevel }}
    >
      {content}
    </Tooltip>
  );
};
